/** @jsx jsx */
import { memo } from 'react';
import { css, jsx } from '@emotion/core';
import { B400, B50, B500, G400, G50, G500, N0, N40, N500, N800, P400, P50, P500, R400, R50, R500, Y500, Y75 } from '@atlaskit/theme/colors';
import { borderRadius, gridSize } from '@atlaskit/theme/constants';
const defaultAppearanceStyles = css({
  backgroundColor: `var(--ds-background-subtleNeutral-resting, ${N40})`,
  color: `var(--ds-text-highEmphasis, ${N500})`
});
const inprogressAppearanceStyles = css({
  backgroundColor: `var(--ds-background-subtleBrand-resting, ${B50})`,
  color: `var(--ds-text-brand, ${B500})`
});
const movedAppearanceStyles = css({
  backgroundColor: `var(--ds-background-subtleWarning-resting, ${Y75})`,
  color: `var(--ds-text-warning, ${N800})`
});
const newAppearanceStyles = css({
  backgroundColor: `var(--ds-background-subtleDiscovery-resting, ${P50})`,
  color: `var(--ds-text-discovery, ${P500})`
});
const removedAppearanceStyles = css({
  backgroundColor: `var(--ds-background-subtleDanger-resting, ${R50})`,
  color: `var(--ds-text-danger, ${R500})`
});
const successAppearanceStyles = css({
  backgroundColor: `var(--ds-background-subtleSuccess-resting, ${G50})`,
  color: `var(--ds-text-success, ${G500})`
});
const defaultBoldAppearanceStyles = css({
  backgroundColor: `var(--ds-background-boldNeutral-resting, ${N500})`,
  color: `var(--ds-text-onBold, ${N0})`
});
const inprogressBoldAppearanceStyles = css({
  backgroundColor: `var(--ds-background-boldBrand-resting, ${B400})`,
  color: `var(--ds-text-onBold, ${N0})`
});
const movedBoldAppearanceStyles = css({
  backgroundColor: `var(--ds-background-boldWarning-resting, ${Y500})`,
  color: `var(--ds-text-onBoldWarning, ${N800})`
});
const newBoldAppearanceStyles = css({
  backgroundColor: `var(--ds-background-boldDiscovery-resting, ${P400})`,
  color: `var(--ds-text-onBold, ${N0})`
});
const removedBoldAppearanceStyles = css({
  backgroundColor: `var(--ds-background-boldDanger-resting, ${R400})`,
  color: `var(--ds-text-onBold, ${N0})`
});
const successBoldAppearanceStyles = css({
  backgroundColor: `var(--ds-background-boldSuccess-resting, ${G400})`,
  color: `var(--ds-text-onBold, ${N0})`
});
const subtleAppearances = {
  default: defaultAppearanceStyles,
  inprogress: inprogressAppearanceStyles,
  moved: movedAppearanceStyles,
  new: newAppearanceStyles,
  removed: removedAppearanceStyles,
  success: successAppearanceStyles
};
const boldAppearances = {
  default: defaultBoldAppearanceStyles,
  inprogress: inprogressBoldAppearanceStyles,
  moved: movedBoldAppearanceStyles,
  new: newBoldAppearanceStyles,
  removed: removedBoldAppearanceStyles,
  success: successBoldAppearanceStyles
};
const contentStyles = css({
  display: 'inline-block',
  boxSizing: 'border-box',
  width: '100%',
  padding: `0 ${gridSize() / 2}px`,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  verticalAlign: 'top',
  whiteSpace: 'nowrap'
});
const containerStyles = css({
  display: 'inline-block',
  boxSizing: 'border-box',
  maxWidth: '100%',
  padding: '2px 0 3px 0',
  borderRadius: borderRadius(),
  fontSize: '11px',
  fontWeight: 700,
  lineHeight: 1,
  textTransform: 'uppercase',
  verticalAlign: 'baseline'
});
/**
 * __Lozenge__
 *
 * A lozenge is a visual indicator used to highlight an item's status for quick recognition.
 *
 * - [Examples](https://atlassian.design/components/lozenge/examples)
 * - [Code](https://atlassian.design/components/lozenge/code)
 * - [Usage](https://atlassian.design/components/lozenge/usage)
 */

const Lozenge = /*#__PURE__*/memo(({
  children,
  testId,
  isBold = false,
  appearance = 'default',
  maxWidth = 200,
  style = {}
}) => jsx("span", {
  style: {
    backgroundColor: style.backgroundColor,
    color: style.color
  },
  css: [isBold ? boldAppearances[appearance] : subtleAppearances[appearance], containerStyles],
  "data-testid": testId
}, jsx("span", {
  style: {
    maxWidth
  },
  css: contentStyles
}, children)));
Lozenge.displayName = 'Lozenge';
export default Lozenge;